/**
 * FtpSelectionStep - Step 2 of match creation wizard
 *
 * Displays FTP targets grouped by default status. Both groups are selectable
 * via checkboxes; the grouping is presentation, not permission:
 * - Default targets: pre-ticked on a new match, and can be unticked
 * - Available targets: unticked to start
 *
 * Every ticked destination carries its own style, IPTC template and
 * photographer scope, defaults included.
 */

import { useState, useEffect } from 'react';
import Spinner from '../../../../shared/components/ui/Spinner/Spinner';
import styles from './MatchModal.module.css';

/**
 * FTP target card component
 *
 * The card body is a <label> so clicking anywhere on it toggles the checkbox.
 * The style picker and the company-profile toggle deliberately sit OUTSIDE that
 * label - a <select> or a second checkbox nested in the card label would
 * forward its clicks to the selection checkbox.
 *
 * @param {Object} props
 * @param {Object} props.target - FTP target row
 * @param {boolean} props.isDefault - Company default: pre-ticked, still declinable
 * @param {boolean} props.isSelected - Whether the target is selected
 * @param {(targetId: string) => void} props.onToggle - Selection toggle
 * @param {Array} [props.grades] - Available grades (export styles)
 * @param {string|null} [props.gradeId] - Currently bound grade for this target
 * @param {(targetId: string, gradeId: string|null) => void} [props.onGradeChange]
 * @param {boolean} [props.includeProfile] - Stack the style on the company profile
 * @param {(targetId: string, include: boolean) => void} [props.onIncludeProfileChange]
 * @param {Array} [props.iptcTemplates] - Available IPTC templates
 * @param {string|null} [props.iptcTemplateId] - Currently bound template
 * @param {(targetId: string, templateId: string|null) => void} [props.onIptcChange]
 * @param {Array} [props.photographers] - Photographers assigned to this match
 * @param {string[]} [props.scopedPhotographerIds] - Empty means everyone
 * @param {(targetId: string, userIds: string[]) => void} [props.onPhotographersChange]
 * @param {Object} [props.referenceOverrides] - This destination's iptc_overrides
 * @param {(targetId: string, field: string, value: string) => void} [props.onReferenceChange]
 */
function FtpTargetCard({
  target,
  isDefault,
  isSelected,
  onToggle,
  grades = [],
  gradeId = null,
  onGradeChange,
  includeProfile = true,
  onIncludeProfileChange,
  iptcTemplates = [],
  iptcTemplateId = null,
  onIptcChange,
  photographers = [],
  scopedPhotographerIds = [],
  onPhotographersChange,
  referenceOverrides = {},
  onReferenceChange,
}) {
  // Every destination in play has a per-match row now, defaults included, so a
  // default can carry a style, a template and a scope like any other. It used
  // to have no row at all, which is why the destination receiving everything
  // was the one that could never be configured.
  // A default is pre-ticked, not compulsory: some jobs are not bound for the
  // house FTP, and a destination you cannot decline is not a default.
  const isActive = isSelected;
  const showGradePicker = isActive && grades.length > 0;
  const showIptcPicker = isActive && iptcTemplates.length > 0;
  // Scoping is only meaningful with someone to scope to, and only worth showing
  // once more than one photographer is on the match.
  const showPhotographerPicker = isActive && photographers.length > 1;
  // The profile toggle only means something under a style: without one the
  // export is the company profile, so there is nothing to opt out of.
  const showProfileToggle = showGradePicker && !!gradeId;
  // A destination declares whether it needs a per-match code by carrying a
  // label for one. Asked here, when the destination is picked, rather than
  // nine fields deep in step 3 -- which is where a wire reference gets
  // forgotten, and you only find out when the receiving end complains.
  const referenceSpec = target.reference_spec || null;
  const showReference = isActive && !!referenceSpec;
  // The KEY is whatever field this destination declared, not a fixed name.
  const referenceValue = referenceSpec ? referenceOverrides[referenceSpec.field] || '' : '';
  const referenceMissing = showReference && !String(referenceValue).trim();

  return (
    <div
      className={`${styles.ftpCard} ${isDefault ? styles.ftpCardDefault : ''} ${isSelected ? styles.ftpCardSelected : ''}`}
    >
      <label className={styles.ftpCardMain}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={isSelected}
          onChange={() => onToggle(target.id)}
        />
        <div className={styles.ftpCardContent}>
          <div className={styles.ftpCardHeader}>
            <span className={styles.ftpCardName}>{target.name}</span>
            {isDefault && <span className={styles.defaultBadge}>Default</span>}
            {target.selects_only && (
              <span
                className={styles.selectsBadge}
                title="Receives only frames the editor explicitly sends (Done + Wire) — plain Done never delivers here"
              >
                Selects only
              </span>
            )}
            {target.live && (
              <span
                className={styles.liveBadge}
                title="Live folder: every ingested frame of this match ships here automatically, bare (no IPTC), as soon as the full-size edit exists — Done later replaces the live file with the finished export"
              >
                Live
              </span>
            )}
          </div>
          <span className={styles.ftpCardHost}>{target.host}</span>
        </div>
      </label>

      {showReference && (
        <label className={styles.ftpCardGrade}>
          <span className={styles.ftpCardGradeLabel}>
            {referenceSpec.label}
            {referenceMissing && <span className={styles.ftpCardWarnDot}>needed</span>}
          </span>
          {/* Typed from the same spec the API validates against, so a wire that
              wants a number cannot be given "high" from this form. */}
          <input
            type={referenceSpec.type === 'int' ? 'number' : 'text'}
            min={referenceSpec.min ?? undefined}
            max={referenceSpec.max ?? undefined}
            maxLength={referenceSpec.max_length ?? undefined}
            className={`${styles.ftpCardReferenceInput} ${
              referenceMissing ? styles.ftpCardReferenceMissing : ''
            }`}
            value={referenceValue}
            onChange={(e) => onReferenceChange?.(target.id, referenceSpec.field, e.target.value)}
            placeholder={`${referenceSpec.label} for this match`}
          />
        </label>
      )}

      {showGradePicker && (
        <label className={styles.ftpCardGrade}>
          <span className={styles.ftpCardGradeLabel}>Style</span>
          <select
            className={styles.ftpCardGradeSelect}
            value={gradeId || ''}
            onChange={(e) => onGradeChange?.(target.id, e.target.value || null)}
          >
            {/* NOT "as shot": the base render always carries the company
                profile (or the config preset), and there is no unprofiled
                path through the pipeline. Only a GRADED destination can opt
                out of the profile, which is why the toggle below appears with
                a style and not without one. */}
            <option value="">No style (company profile)</option>
            {grades.map((grade) => (
              <option key={grade.id} value={grade.id}>
                {grade.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {showIptcPicker && (
        <label className={styles.ftpCardGrade}>
          <span className={styles.ftpCardGradeLabel}>IPTC</span>
          <select
            className={styles.ftpCardGradeSelect}
            value={iptcTemplateId || ''}
            onChange={(e) => onIptcChange?.(target.id, e.target.value || null)}
          >
            <option value="">House default</option>
            {iptcTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
                {tpl.credit ? ` — ${tpl.credit}` : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {showPhotographerPicker && (
        <div className={styles.ftpCardProfile}>
          <span className={styles.ftpCardGradeLabel}>Photographers</span>
          {photographers.map((p) => {
            const checked = scopedPhotographerIds.includes(String(p.id));
            return (
              <label key={p.id} className={styles.ftpCardProfileToggle}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={checked}
                  onChange={() => {
                    const id = String(p.id);
                    const next = checked
                      ? scopedPhotographerIds.filter((x) => x !== id)
                      : [...scopedPhotographerIds, id];
                    onPhotographersChange?.(target.id, next);
                  }}
                />
                <span className={styles.ftpCardProfileLabel}>
                  {p.full_name || p.username}
                </span>
              </label>
            );
          })}
          <span className={styles.ftpCardProfileHint}>
            {scopedPhotographerIds.length === 0
              ? 'Everyone. Tick names to send only their frames here.'
              : `Only these ${scopedPhotographerIds.length} photographer(s) reach this destination.`}
          </span>
        </div>
      )}

      {showProfileToggle && (
        <div className={styles.ftpCardProfile}>
          <label className={styles.ftpCardProfileToggle}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={includeProfile}
              onChange={(e) => onIncludeProfileChange?.(target.id, e.target.checked)}
            />
            <span className={styles.ftpCardProfileLabel}>Include company profile</span>
          </label>
          <span className={styles.ftpCardProfileHint}>
            When off, the style is applied to the original without the company
            profile. Editor corrections still apply: an exposure fix is a fix
            whatever the style.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * FtpSelectionStep component
 *
 * @param {Object} props
 * @param {string[]} props.selectedIds - Array of selected FTP target IDs
 * @param {(ids: string[]) => void} props.onChange - Callback when selection changes
 * @param {Function} props.fetchFtpTargets - API function to fetch FTP targets
 * @param {Function} [props.fetchGrades] - API function to fetch grades (export styles)
 * @param {Object} [props.gradeByTarget] - Map of {ftp_target_id: grade_id|null}
 * @param {(targetId: string, gradeId: string|null) => void} [props.onGradeChange]
 * @param {Object} [props.includeProfileByTarget] - Map of {ftp_target_id: boolean}
 * @param {(targetId: string, include: boolean) => void} [props.onIncludeProfileChange]
 * @param {Function} [props.fetchIptcTemplates] - API function to fetch IPTC templates
 * @param {boolean} [props.autoSelectDefaults] - Seed defaults into the selection (new match only)
 * @param {Object} [props.iptcByTarget] - Map of {ftp_target_id: template_id|null}
 * @param {(targetId: string, templateId: string|null) => void} [props.onIptcChange]
 * @param {Array} [props.photographers] - Photographers assigned to this match
 * @param {Object} [props.photographersByTarget] - Map of {ftp_target_id: [user_id]}
 * @param {(targetId: string, userIds: string[]) => void} [props.onPhotographersChange]
 */
export default function FtpSelectionStep({
  selectedIds,
  onChange,
  fetchFtpTargets,
  fetchGrades,
  gradeByTarget = {},
  onGradeChange,
  includeProfileByTarget = {},
  onIncludeProfileChange,
  fetchIptcTemplates,
  autoSelectDefaults = false,
  iptcByTarget = {},
  onIptcChange,
  photographers = [],
  photographersByTarget = {},
  onPhotographersChange,
  overridesByTarget = {},
  onReferenceChange,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [available, setAvailable] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gradesError, setGradesError] = useState(null);
  const [iptcTemplates, setIptcTemplates] = useState([]);

  useEffect(() => {
    let mounted = true;

    async function loadTargets() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFtpTargets();
        if (mounted) {
          const defaultTargets = data.defaults || [];
          setDefaults(defaultTargets);
          setAvailable(data.available || []);

          // Seed the selection with the defaults, but ONLY when creating. On
          // edit the selection comes from the saved rows, and re-adding a
          // default here would silently undo an editor's deliberate untick.
          if (autoSelectDefaults && defaultTargets.length > 0) {
            const seeded = defaultTargets
              .map((t) => String(t.id))
              .filter((id) => !selectedIds.includes(id));
            if (seeded.length > 0) onChange([...selectedIds, ...seeded]);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err.message || 'Failed to load FTP targets');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadTargets();

    return () => {
      mounted = false;
    };
    // Deliberately keyed on the fetcher alone: this must run once per mount.
    // Including selectedIds would re-seed defaults after every untick, which is
    // exactly the behaviour being removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFtpTargets]);

  // Grades load independently from targets: a failure here must never block
  // destination selection, it just means no style pickers are offered.
  useEffect(() => {
    if (!fetchGrades) return undefined;

    let mounted = true;

    async function loadGrades() {
      try {
        setGradesError(null);
        const data = await fetchGrades();
        if (mounted) {
          // The endpoint is deliberately unpaginated, but the project default
          // is not. Reading results[] as well means a pagination change on the
          // server empties the picker loudly in tests, not silently in prod.
          setGrades(Array.isArray(data) ? data : (data?.results ?? []));
        }
      } catch (err) {
        if (mounted) {
          setGrades([]);
          setGradesError(err.message || 'Failed to load styles');
        }
      }
    }

    loadGrades();

    return () => {
      mounted = false;
    };
  }, [fetchGrades]);

  // Templates load independently, like grades: a failure here must never block
  // destination selection, it just means no IPTC pickers are offered and every
  // destination writes the house default.
  useEffect(() => {
    if (!fetchIptcTemplates) return undefined;

    let mounted = true;

    async function loadTemplates() {
      try {
        const data = await fetchIptcTemplates();
        if (mounted) {
          setIptcTemplates(Array.isArray(data) ? data : (data?.results ?? []));
        }
      } catch {
        if (mounted) setIptcTemplates([]);
      }
    }

    loadTemplates();

    return () => {
      mounted = false;
    };
  }, [fetchIptcTemplates]);

  const handleToggle = (targetId) => {
    const idStr = String(targetId);
    if (selectedIds.includes(idStr)) {
      onChange(selectedIds.filter((id) => id !== idStr));
    } else {
      onChange([...selectedIds, idStr]);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="md" />
        <span>Loading FTP destinations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    );
  }

  const totalCount = selectedIds.length;

  return (
    <div className={styles.ftpSelectionStep}>
      <p className={styles.stepDescription}>
        Select which FTP destinations should receive photos from this match.
        Default destinations start ticked and can be unticked.
      </p>

      {/* Default Targets Section */}
      {defaults.length > 0 && (
        <div className={styles.ftpSection}>
          <h4 className={styles.ftpSectionTitle}>
            Default Destinations ({defaults.length})
          </h4>
          <p className={styles.ftpSectionHint}>
            Ticked by default on a new match, and configurable per match like any
            other. Untick one to keep this match out of it.
          </p>
          <div className={styles.ftpGrid}>
            {defaults.map((target) => (
              <FtpTargetCard
                key={target.id}
                target={target}
                isDefault={true}
                isSelected={selectedIds.includes(String(target.id))}
                onToggle={handleToggle}
                grades={grades}
                gradeId={gradeByTarget[target.id] || null}
                onGradeChange={onGradeChange}
                includeProfile={includeProfileByTarget[target.id] !== false}
                onIncludeProfileChange={onIncludeProfileChange}
                iptcTemplates={iptcTemplates}
                iptcTemplateId={iptcByTarget[target.id] || null}
                onIptcChange={onIptcChange}
                photographers={photographers}
                scopedPhotographerIds={photographersByTarget[target.id] || []}
                referenceOverrides={overridesByTarget[String(target.id)] || {}}
                onReferenceChange={onReferenceChange}
                onPhotographersChange={onPhotographersChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Targets Section */}
      {available.length > 0 && (
        <div className={styles.ftpSection}>
          <h4 className={styles.ftpSectionTitle}>
            Additional Destinations ({available.length})
          </h4>
          <p className={styles.ftpSectionHint}>
            Select additional destinations for this specific match.
          </p>
          {gradesError && (
            <p className={styles.ftpSectionHint}>
              Styles could not be loaded. Destinations can still be selected.
            </p>
          )}
          <div className={styles.ftpGrid}>
            {available.map((target) => (
              <FtpTargetCard
                key={target.id}
                target={target}
                isDefault={false}
                isSelected={selectedIds.includes(String(target.id))}
                onToggle={handleToggle}
                grades={grades}
                gradeId={gradeByTarget[String(target.id)] || null}
                onGradeChange={onGradeChange}
                // Missing entry means the default: include the company profile.
                includeProfile={includeProfileByTarget[String(target.id)] !== false}
                onIncludeProfileChange={onIncludeProfileChange}
                iptcTemplates={iptcTemplates}
                iptcTemplateId={iptcByTarget[String(target.id)] || null}
                onIptcChange={onIptcChange}
                photographers={photographers}
                scopedPhotographerIds={photographersByTarget[String(target.id)] || []}
                referenceOverrides={overridesByTarget[String(target.id)] || {}}
                onReferenceChange={onReferenceChange}
                onPhotographersChange={onPhotographersChange}
              />
            ))}
          </div>
        </div>
      )}

      {/* No targets message */}
      {defaults.length === 0 && available.length === 0 && (
        <div className={styles.noTargets}>
          <p>No FTP destinations configured for your company.</p>
          <p className={styles.noTargetsHint}>
            You can add FTP destinations in Settings {'>'} FTP Targets.
          </p>
        </div>
      )}

      {/* Summary */}
      <div className={styles.ftpSummary}>
        <span className={styles.ftpSummaryCount}>
          {totalCount} destination{totalCount !== 1 ? 's' : ''} selected
        </span>
      </div>
    </div>
  );
}
